import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Drawer, Input, InputNumber, Select, Space, Table, Tag, Typography, message, theme } from 'antd'
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { resolveFrontendApiBase } from '../utils/apiBase'
import adminAxios from './adminAxios'
import './AdminDashboardSurfaces.css'

const API_BASE = resolveFrontendApiBase()
const { Title, Text } = Typography

type TimeRange = '7d' | '30d' | '90d'
type BreakdownDimension = 'process' | 'stage' | 'operation' | 'provider' | 'model' | 'user' | 'book' | 'language_pair' | 'voice'

type CostSummary = {
  eventCount?: number
  providerCostUsdActual?: number
  providerCostUsdEstimated?: number
  providerCostUsdEffective?: number
  chargedCredits?: number
  revenueEquivalentUsd?: number
  marginUsd?: number
  missingProviderCostCount?: number
  missingCreditChargeCount?: number
  missingContextCount?: number
  missingCostRate?: number
}

type CostTrend = CostSummary & {
  date: string
  processProviderCostUsd?: Record<string, number>
}

type BreakdownRow = CostSummary & {
  key: string
  label: string
  uniqueBooks?: number
  uniqueUsers?: number
}

type CostEvent = CostSummary & {
  eventId: string
  sourceCollection?: string
  sourceEventId?: string
  occurredAt?: string
  day?: string
  process?: string
  stageName?: string
  operation?: string
  status?: string
  provider?: string
  configuredModelId?: string
  apiModel?: string
  modality?: string
  userId?: string
  bookId?: string
  chapterGuid?: string
  runId?: string
  languagePair?: string
  targetLanguage?: string
  voice?: string
  coverHistoryId?: string
  inputTokens?: number
  cachedTokens?: number
  outputTokens?: number
  totalTokens?: number
  inputCharacters?: number
  imageCount?: number
  audioDurationSeconds?: number
  audioBytes?: number
  costSource?: string
  costConfidence?: string
  reconciliationFlags?: string[]
}

type JobRow = {
  job_id: string
  status: string
  created_at?: string
  finished_at?: string
  request?: Record<string, any>
  counts?: Record<string, any>
}

type ScheduleRow = {
  scheduleKey: string
  label: string
  cadenceLabel?: string
  nextRunAt?: string
  lastRunAt?: string
  lastStatus?: string
  lastJobId?: string
}

const formatCurrency = (value?: number | null): string => {
  if (value === null || value === undefined) return '-'
  const normalized = Number(value || 0)
  if (Math.abs(normalized) < 1) return `$${normalized.toFixed(4)}`
  return `$${normalized.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
}

const formatNumber = (value?: number | null): string => Number(value || 0).toLocaleString()
const formatPercent = (value?: number | null): string => `${((Number(value || 0)) * 100).toFixed(1)}%`
const formatDateTime = (value?: string | null): string => value ? new Date(value).toLocaleString() : '-'

const todayIso = () => new Date().toISOString().slice(0, 10)

const computeDateWindow = (range: TimeRange) => {
  const end = new Date()
  const start = new Date(end)
  const days = range === '7d' ? 7 : range === '90d' ? 90 : 30
  start.setDate(end.getDate() - days + 1)
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) }
}

const PROCESS_LABELS: Record<string, string> = {
  translation: 'Translation',
  proofreading: 'Proofreading',
  audio_generation: 'Audio',
  cover_generation: 'Cover',
}

const AICostsTab: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const { token } = theme.useToken()
  const defaultWindow = useMemo(() => computeDateWindow('30d'), [])
  const [timeRange, setTimeRange] = useState<TimeRange>('30d')
  const [startDate, setStartDate] = useState(defaultWindow.start)
  const [endDate, setEndDate] = useState(defaultWindow.end)
  const [overview, setOverview] = useState<any>(null)
  const [breakdowns, setBreakdowns] = useState<BreakdownRow[]>([])
  const [events, setEvents] = useState<CostEvent[]>([])
  const [forecast, setForecast] = useState<any>(null)
  const [reconciliation, setReconciliation] = useState<any>(null)
  const [jobs, setJobs] = useState<JobRow[]>([])
  const [schedules, setSchedules] = useState<ScheduleRow[]>([])
  const [dimension, setDimension] = useState<BreakdownDimension>('process')
  const [processFilter, setProcessFilter] = useState<string | undefined>(undefined)
  const [selectedEvent, setSelectedEvent] = useState<CostEvent | null>(null)
  const [loading, setLoading] = useState(false)
  const [jobLoading, setJobLoading] = useState(false)
  const [monthlyGrowthPct, setMonthlyGrowthPct] = useState(0)
  const [providerAdjustmentPct, setProviderAdjustmentPct] = useState(0)
  const [modelAdjustmentPct, setModelAdjustmentPct] = useState(0)
  const [processMixOverride, setProcessMixOverride] = useState('')
  const [forecastDays, setForecastDays] = useState(90)

  const rangeParams = useMemo(() => ({ start: startDate, end: endDate }), [startDate, endDate])

  const cardStyle = useMemo(
    () => ({
      border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: 8,
      padding: 16,
      background: token.colorBgContainer,
    }),
    [token]
  )
  const gridStyle = useMemo(() => ({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }), [])
  const chartAxisStyle = useMemo(() => ({ stroke: token.colorTextTertiary, fontSize: 12, fill: token.colorTextTertiary }), [token])

  const loadData = useCallback(async () => {
    if (!isAdmin) return
    setLoading(true)
    try {
      const [overviewRes, breakdownRes, eventsRes, forecastRes, reconciliationRes, jobsRes, schedulesRes] = await Promise.all([
        adminAxios.get(`${API_BASE}/admin/ai-costs/overview`, { params: rangeParams }),
        adminAxios.get(`${API_BASE}/admin/ai-costs/breakdowns`, { params: { ...rangeParams, dimension, process: processFilter } }),
        adminAxios.get(`${API_BASE}/admin/ai-costs/events`, { params: { ...rangeParams, process: processFilter, limit: 50 } }),
        adminAxios.get(`${API_BASE}/admin/ai-costs/forecast`, {
          params: {
            forecast_days: forecastDays,
            monthly_growth_pct: monthlyGrowthPct,
            provider_price_adjustment_pct: providerAdjustmentPct,
            model_price_adjustment_pct: modelAdjustmentPct,
            process_mix_override: processMixOverride.trim() || undefined,
          },
        }),
        adminAxios.get(`${API_BASE}/admin/ai-costs/reconciliation`, { params: rangeParams }),
        adminAxios.get(`${API_BASE}/admin/ai-costs/jobs`, { params: { limit: 10 } }),
        adminAxios.get(`${API_BASE}/admin/ai-costs/schedules`),
      ])
      setOverview(overviewRes.data)
      setBreakdowns(breakdownRes.data?.items || [])
      setEvents(eventsRes.data?.items || [])
      setForecast(forecastRes.data)
      setReconciliation(reconciliationRes.data)
      setJobs(jobsRes.data?.items || [])
      setSchedules(schedulesRes.data?.items || [])
    } catch (error: any) {
      message.error(error?.response?.data?.detail || error?.message || 'Failed to load AI cost analytics')
    } finally {
      setLoading(false)
    }
  }, [dimension, forecastDays, isAdmin, modelAdjustmentPct, monthlyGrowthPct, processFilter, processMixOverride, providerAdjustmentPct, rangeParams])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const runRebuild = useCallback(async () => {
    setJobLoading(true)
    try {
      const response = await adminAxios.post(`${API_BASE}/admin/ai-costs/jobs/rebuild`, {
        dry_run: false,
        start_date: startDate,
        end_date: endDate,
        include_model_usage_fallback: true,
      })
      message.success(`AI cost rebuild queued: ${response.data?.job_id || 'job created'}`)
      await loadData()
    } catch (error: any) {
      message.error(error?.response?.data?.detail || error?.message || 'Failed to queue AI cost rebuild')
    } finally {
      setJobLoading(false)
    }
  }, [endDate, loadData, startDate])

  const setRangeFromPreset = (range: TimeRange) => {
    const next = computeDateWindow(range)
    setTimeRange(range)
    setStartDate(next.start)
    setEndDate(next.end)
  }

  const summary = overview?.summary || {}
  const forecastTotals = forecast?.totals || {}
  const trendData: CostTrend[] = overview?.trends || []
  const forecastData = forecast?.items || []
  const combinedTrendData = [
    ...trendData.map((row) => ({ ...row, kind: 'Actual' })),
    ...forecastData.map((row: any) => ({ ...row, kind: 'Forecast' })),
  ]

  const breakdownColumns = [
    { title: 'Label', dataIndex: 'label', key: 'label' },
    { title: 'Events', dataIndex: 'eventCount', key: 'eventCount', render: formatNumber },
    { title: 'Provider USD', dataIndex: 'providerCostUsdEffective', key: 'providerCostUsdEffective', render: formatCurrency },
    { title: 'Credits', dataIndex: 'chargedCredits', key: 'chargedCredits', render: formatNumber },
    { title: 'Revenue Eq.', dataIndex: 'revenueEquivalentUsd', key: 'revenueEquivalentUsd', render: formatCurrency },
    { title: 'Margin', dataIndex: 'marginUsd', key: 'marginUsd', render: formatCurrency },
    { title: 'Missing Cost', dataIndex: 'missingCostRate', key: 'missingCostRate', render: formatPercent },
  ]

  const eventColumns = [
    { title: 'When', dataIndex: 'occurredAt', key: 'occurredAt', render: formatDateTime },
    { title: 'Process', dataIndex: 'process', key: 'process', render: (value: string) => PROCESS_LABELS[value] || value || '-' },
    { title: 'Stage / Operation', key: 'stage', render: (_: any, row: CostEvent) => row.stageName || row.operation || '-' },
    { title: 'Provider', dataIndex: 'provider', key: 'provider' },
    { title: 'Model', key: 'model', render: (_: any, row: CostEvent) => row.configuredModelId || row.apiModel || '-' },
    { title: 'Provider USD', dataIndex: 'providerCostUsdEffective', key: 'providerCostUsdEffective', render: formatCurrency },
    { title: 'Flags', key: 'flags', render: (_: any, row: CostEvent) => (row.reconciliationFlags || []).map((flag) => <Tag key={flag}>{flag}</Tag>) },
    { title: '', key: 'action', render: (_: any, row: CostEvent) => <Button size="small" onClick={() => setSelectedEvent(row)}>Details</Button> },
  ]

  if (!isAdmin) {
    return (
      <Card className="admin-dashboard-surface-card">
        <Title level={3}>AI Costs</Title>
        <Text>Admin access is required.</Text>
      </Card>
    )
  }

  return (
    <div className="admin-dashboard-surface">
      <Card title="AI Costs" className="admin-dashboard-surface-card">
        <Space wrap size="middle">
          <Select value={timeRange} onChange={setRangeFromPreset} style={{ width: 140 }} options={[
            { value: '7d', label: 'Last 7 days' },
            { value: '30d', label: 'Last 30 days' },
            { value: '90d', label: 'Last 90 days' },
          ]} />
          <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value || todayIso())} style={{ width: 150 }} />
          <Input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value || todayIso())} style={{ width: 150 }} />
          <Button type="primary" onClick={() => void loadData()} loading={loading}>Refresh</Button>
          <Button onClick={() => void runRebuild()} loading={jobLoading}>Rebuild Cost Facts</Button>
        </Space>
      </Card>

      <Card title="Overview" loading={loading} className="admin-dashboard-surface-card">
        <div style={gridStyle}>
          <div style={cardStyle}><Text type="secondary">Provider USD</Text><Title level={3}>{formatCurrency(summary.providerCostUsdEffective)}</Title></div>
          <div style={cardStyle}><Text type="secondary">Charged Credits</Text><Title level={3}>{formatNumber(summary.chargedCredits)}</Title></div>
          <div style={cardStyle}><Text type="secondary">Revenue Equivalent</Text><Title level={3}>{formatCurrency(summary.revenueEquivalentUsd)}</Title></div>
          <div style={cardStyle}><Text type="secondary">Margin Estimate</Text><Title level={3}>{formatCurrency(summary.marginUsd)}</Title></div>
          <div style={cardStyle}><Text type="secondary">Missing Cost Rate</Text><Title level={3}>{formatPercent(summary.missingCostRate)}</Title></div>
          <div style={cardStyle}><Text type="secondary">90-Day Forecast</Text><Title level={3}>{formatCurrency(forecastTotals.providerCostUsd)}</Title></div>
        </div>
        <div className="admin-analytics-chart-scroll" style={{ marginTop: 24 }}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={combinedTrendData}>
              <CartesianGrid stroke={token.colorBorderSecondary} strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={chartAxisStyle} />
              <YAxis tick={chartAxisStyle} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="providerCostUsdEffective" stroke={token.colorPrimary} name="Actual Provider USD" />
              <Line type="monotone" dataKey="providerCostUsd" stroke={token.colorWarning} name="Forecast Provider USD" />
              <Line type="monotone" dataKey="marginUsd" stroke={token.colorSuccess} name="Margin" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="Forecast Scenario" className="admin-dashboard-surface-card">
        <Space wrap size="middle">
          <label>Days <InputNumber min={1} max={365} value={forecastDays} onChange={(value) => setForecastDays(Number(value || 90))} /></label>
          <label>Monthly Growth % <InputNumber value={monthlyGrowthPct} onChange={(value) => setMonthlyGrowthPct(Number(value || 0))} /></label>
          <label>Provider Price % <InputNumber value={providerAdjustmentPct} onChange={(value) => setProviderAdjustmentPct(Number(value || 0))} /></label>
          <label>Model Price % <InputNumber value={modelAdjustmentPct} onChange={(value) => setModelAdjustmentPct(Number(value || 0))} /></label>
          <label>Process Mix <Input value={processMixOverride} onChange={(event) => setProcessMixOverride(event.target.value)} placeholder='{"audio_generation":0.5}' style={{ width: 220 }} /></label>
          <Button onClick={() => void loadData()} loading={loading}>Run Forecast</Button>
        </Space>
        {forecast?.warnings?.length ? (
          <Alert style={{ marginTop: 16 }} type={forecast.confidence === 'low' ? 'warning' : 'info'} showIcon message={`Forecast confidence: ${forecast.confidence}`} description={forecast.warnings.join(' ')} />
        ) : null}
      </Card>

      <Card title="Breakdowns" loading={loading} className="admin-dashboard-surface-card">
        <Space wrap size="middle" style={{ marginBottom: 16 }}>
          <Select value={dimension} onChange={setDimension} style={{ width: 170 }} options={[
            'process', 'stage', 'operation', 'provider', 'model', 'user', 'book', 'language_pair', 'voice',
          ].map((value) => ({ value, label: value.replace('_', ' ') }))} />
          <Select allowClear placeholder="Process" value={processFilter} onChange={setProcessFilter} style={{ width: 180 }} options={[
            { value: 'translation', label: 'Translation' },
            { value: 'proofreading', label: 'Proofreading' },
            { value: 'audio_generation', label: 'Audio' },
            { value: 'cover_generation', label: 'Cover' },
          ]} />
        </Space>
        <div className="admin-analytics-chart-scroll">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={breakdowns}>
              <CartesianGrid stroke={token.colorBorderSecondary} strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={chartAxisStyle} />
              <YAxis tick={chartAxisStyle} />
              <Tooltip />
              <Legend />
              <Bar dataKey="providerCostUsdEffective" fill={token.colorPrimary} name="Provider USD" />
              <Bar dataKey="marginUsd" fill={token.colorSuccess} name="Margin" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <Table rowKey="key" dataSource={breakdowns} columns={breakdownColumns} pagination={{ pageSize: 10 }} scroll={{ x: 900 }} />
      </Card>

      <Card title="Event Drilldown" loading={loading} className="admin-dashboard-surface-card">
        <Table rowKey="eventId" dataSource={events} columns={eventColumns} pagination={{ pageSize: 10 }} scroll={{ x: 1100 }} />
      </Card>

      <Card title="Data Quality" loading={loading} className="admin-dashboard-surface-card">
        <div style={gridStyle}>
          <div style={cardStyle}><Text type="secondary">Missing Provider Cost</Text><Title level={4}>{formatNumber(reconciliation?.summary?.missingProviderCostCount)}</Title></div>
          <div style={cardStyle}><Text type="secondary">Missing Credit Charge</Text><Title level={4}>{formatNumber(reconciliation?.summary?.missingCreditChargeCount)}</Title></div>
          <div style={cardStyle}><Text type="secondary">Missing Context</Text><Title level={4}>{formatNumber(reconciliation?.summary?.missingContextCount)}</Title></div>
        </div>
        <Table
          style={{ marginTop: 16 }}
          rowKey="key"
          dataSource={reconciliation?.flags || []}
          columns={[{ title: 'Flag', dataIndex: 'key', key: 'key' }, { title: 'Count', dataIndex: 'count', key: 'count', render: formatNumber }]}
          pagination={false}
        />
      </Card>

      <Card title="Jobs" loading={jobLoading} className="admin-dashboard-surface-card">
        <Table
          rowKey="scheduleKey"
          dataSource={schedules}
          columns={[
            { title: 'Schedule', dataIndex: 'label', key: 'label' },
            { title: 'Cadence', dataIndex: 'cadenceLabel', key: 'cadenceLabel' },
            { title: 'Next', dataIndex: 'nextRunAt', key: 'nextRunAt', render: formatDateTime },
            { title: 'Last', dataIndex: 'lastRunAt', key: 'lastRunAt', render: formatDateTime },
            { title: 'Status', dataIndex: 'lastStatus', key: 'lastStatus', render: (value: string) => value ? <Tag>{value}</Tag> : '-' },
            { title: 'Last Job', dataIndex: 'lastJobId', key: 'lastJobId' },
          ]}
          pagination={false}
          scroll={{ x: 900 }}
        />
        <Table
          style={{ marginTop: 16 }}
          rowKey="job_id"
          dataSource={jobs}
          columns={[
            { title: 'Job', dataIndex: 'job_id', key: 'job_id' },
            { title: 'Status', dataIndex: 'status', key: 'status', render: (value: string) => <Tag>{value}</Tag> },
            { title: 'Created', dataIndex: 'created_at', key: 'created_at', render: formatDateTime },
            { title: 'Finished', dataIndex: 'finished_at', key: 'finished_at', render: formatDateTime },
            { title: 'Written', key: 'written', render: (_: any, row: JobRow) => formatNumber(row.counts?.eventsWritten) },
          ]}
          pagination={false}
          scroll={{ x: 900 }}
        />
      </Card>

      <Drawer title="AI Cost Event" open={!!selectedEvent} onClose={() => setSelectedEvent(null)} width={560}>
        {selectedEvent ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <Text><strong>Source:</strong> {selectedEvent.sourceCollection}:{selectedEvent.sourceEventId}</Text>
            <Text><strong>Process:</strong> {selectedEvent.process}</Text>
            <Text><strong>Stage / Operation:</strong> {selectedEvent.stageName || selectedEvent.operation || '-'}</Text>
            <Text><strong>Provider / Model:</strong> {selectedEvent.provider || '-'} / {selectedEvent.configuredModelId || selectedEvent.apiModel || '-'}</Text>
            <Text><strong>User / Book:</strong> {selectedEvent.userId || '-'} / {selectedEvent.bookId || '-'}</Text>
            <Text><strong>Run / Chapter:</strong> {selectedEvent.runId || '-'} / {selectedEvent.chapterGuid || '-'}</Text>
            <Text><strong>Provider USD:</strong> {formatCurrency(selectedEvent.providerCostUsdEffective)}</Text>
            <Text><strong>Credits:</strong> {formatNumber(selectedEvent.chargedCredits)}</Text>
            <Text><strong>Revenue Eq:</strong> {formatCurrency(selectedEvent.revenueEquivalentUsd)}</Text>
            <Text><strong>Margin:</strong> {formatCurrency(selectedEvent.marginUsd)}</Text>
            <Text><strong>Usage:</strong> tokens {formatNumber(selectedEvent.totalTokens)}, chars {formatNumber(selectedEvent.inputCharacters)}, images {formatNumber(selectedEvent.imageCount)}, audio seconds {formatNumber(selectedEvent.audioDurationSeconds)}</Text>
            <Text><strong>Cost Source:</strong> {selectedEvent.costSource || '-'} ({selectedEvent.costConfidence || '-'})</Text>
            <div>{(selectedEvent.reconciliationFlags || []).map((flag) => <Tag key={flag}>{flag}</Tag>)}</div>
          </div>
        ) : null}
      </Drawer>
    </div>
  )
}

export default AICostsTab
